/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, createContext } from 'react';
import PropTypes from "prop-types";
import { css } from '@emotion/react';
import classnames from 'classnames';
import { tabbable } from 'tabbable';
import { logicalCSS } from '../../global_styling';
import { keys, useCombinedRefs, useEuiTheme } from '../../services';
import { useResizeObserver } from '../observer/resize_observer';
import { EuiFocusTrap } from '../focus_trap';
import { euiFormMaxWidth } from '../form/form.styles';
import { EuiPopover } from './popover';
// Used by child components that want to know the parent popover width
export var EuiInputPopoverWidthContext = /*#__PURE__*/createContext(0);
export var EuiInputPopover = ({
  children,
  className,
  closePopover,
  anchorPosition = 'downLeft',
  attachToAnchor = true,
  repositionToCrossAxis = false,
  display = 'block',
  panelPaddingSize = 's',
  closeOnScroll = false,
  ownFocus = false,
  disableFocusTrap = false,
  focusTrapProps,
  input,
  fullWidth = false,
  panelMinWidth = 0,
  onPanelResize,
  inputRef: _inputRef,
  panelRef: _panelRef,
  ...props
}) => {
  const classes = classnames('euiInputPopover', className);
  const euiTheme = useEuiTheme();
  const formMaxWidth = euiFormMaxWidth(euiTheme);

  /**
   * Ref setup
   */

  const popoverClassRef = useRef(null);
  // The inputEl state ensures that width is correctly tracked on initial load
  const [inputEl, setInputEl] = useState(null);
  // The panelEl state ensures that width is correctly set every time the popover opens
  const [panelEl, setPanelEl] = useState(null);
  const inputRef = useCombinedRefs([setInputEl, _inputRef]);
  const panelRef = useCombinedRefs([setPanelEl, _panelRef]);

  /**
   * Sizing/width logic
   */

  const inputWidth = useResizeObserver(inputEl, 'width').width;
  const panelWidth = useMemo(() => {
    return inputWidth < panelMinWidth ? panelMinWidth : inputWidth;
  }, [panelMinWidth, inputWidth]);

  // Resize callback
  useEffect(() => {
    onPanelResize?.(panelWidth);
  }, [panelWidth, onPanelResize]);
  useEffect(() => {
    if (panelEl) {
      // We have to modify the popover panel DOM node directly instead of using
      // `panelStyle`, as there's some weird positioning bugs on resize otherwise
      panelEl.style.inlineSize = `${panelWidth}px`;
    }
  }, [panelEl, panelWidth]);
  useEffect(() => {
    // This fires on all input width changes regardless of minimum size, because on
    // right/center anchored popovers, the input width affects the position of the popover
    if (panelEl) {
      popoverClassRef.current?.positionPopoverFluid();
    }
  }, [inputWidth, panelEl]);

  /**
   * Popover tab to close logic
   */

  const panelPropsOnKeyDown = props.panelProps?.onKeyDown;
  const onKeyDown = useCallback(event => {
    panelPropsOnKeyDown?.(event);
    if (event.key === keys.TAB) {
      if (disableFocusTrap) {
        if (!ownFocus) {
          closePopover();
        }
      } else {
        const tabbableItems = tabbable(event.currentTarget).filter(el => !el.hasAttribute('data-focus-guard'));
        if (!tabbableItems.length) return;
        const tabbingFromLastItemInPopover = document.activeElement === tabbableItems[tabbableItems.length - 1];
        if (tabbingFromLastItemInPopover) {
          closePopover();
        }
      }
    }
  }, [disableFocusTrap, ownFocus, closePopover, panelPropsOnKeyDown]);

  /**
   * Optional close on scroll behavior
   */

  useEffect(() => {
    // When the popover opens, add a scroll listener to the page (& remove it after)
    if (closeOnScroll && panelEl) {
      const closePopoverOnScroll = event => {
        const scrollTarget = event.target;

        // Basic existence check
        if (!panelEl || !inputEl || !scrollTarget) {
          return;
        }
        // Do not close the popover if the input or popover itself was scrolled
        if (panelEl.contains(scrollTarget) || inputEl.contains(scrollTarget)) {
          return;
        }
        // Firefox will trigger a scroll event in many common situations (e.g. docs side nav)
        // when the options list div is appended to the DOM. To work around this, we should
        // check if the element that scrolled actually contains/will affect the input
        if (!scrollTarget.contains(inputEl)) {
          return;
        }
        closePopover();
      };

      // Kibana Cypress tests trigger a scroll event in many common situations when the options list div is appended
      // to the DOM; in testing it was always within 100ms, but setting a timeout here for 500ms to be safe
      const timeoutId = setTimeout(() => {
        window.addEventListener('scroll', closePopoverOnScroll, {
          passive: true,
          // for better performance as we won't call preventDefault
          capture: true // scroll events don't bubble, they must be captured instead
        });
      }, 500);
      return () => {
        window.removeEventListener('scroll', closePopoverOnScroll, {
          capture: true
        });
        clearTimeout(timeoutId);
      };
    }
  }, [closeOnScroll, closePopover, panelEl, inputEl]);
  return <EuiPopover className={classes} css={/*#__PURE__*/css(fullWidth ? undefined : logicalCSS('max-width', formMaxWidth), ";label:EuiInputPopover;")} display={display} button={input} popoverRef={inputRef} panelRef={panelRef} ref={popoverClassRef} closePopover={closePopover} anchorPosition={anchorPosition} attachToAnchor={attachToAnchor} repositionToCrossAxis={repositionToCrossAxis} panelPaddingSize={panelPaddingSize} ownFocus={ownFocus} {...props} panelProps={{
    ...props.panelProps,
    onKeyDown
  }}>
      <EuiFocusTrap clickOutsideDisables={true} disabled={disableFocusTrap} {...focusTrapProps}>
        <EuiInputPopoverWidthContext.Provider value={panelWidth}>
          {children}
        </EuiInputPopoverWidthContext.Provider>
      </EuiFocusTrap>
    </EuiPopover>;
};
EuiInputPopover.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Alignment of the popover relative to the input
     */
  anchorPosition: PropTypes.oneOf(["downLeft", "downRight", "downCenter"]),
  disableFocusTrap: PropTypes.bool,
  /**
     * Allows automatically closing the input popover on page scroll
     */
  closeOnScroll: PropTypes.bool,
  fullWidth: PropTypes.bool,
  input: PropTypes.any.isRequired,
  inputRef: PropTypes.any,
  onPanelResize: PropTypes.func,
  /**
     * By default, **EuiInputPopovers** inherit the same width as the passed input element.
     * However, if the input width is too small, you can pass a minimum panel width
     * (that should be based on the popover content).
     */
  panelMinWidth: PropTypes.number
};
try {
  EuiInputPopover.__docgenInfo = {
    tags: {},
    filePath: '/app/packages/eui/src/components/popover/input_popover.tsx',
    description: '',
    displayName: 'EuiInputPopover',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + 'Provide a name to the popover panel\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      anchorPosition: {
        defaultValue: {
          value: 'downLeft'
        },
        description: 'Alignment of the popover relative to the input',
        name: 'anchorPosition',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"downCenter" | "downLeft" | "downRight"',
          value: [{
            value: '"downCenter"'
          }, {
            value: '"downLeft"'
          }, {
            value: '"downRight"'
          }]
        }
      },
      disableFocusTrap: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'disableFocusTrap',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      closeOnScroll: {
        defaultValue: {
          value: 'false'
        },
        description: 'Allows automatically closing the input popover on page scroll',
        name: 'closeOnScroll',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      input: {
        defaultValue: null,
        description: '',
        name: 'input',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      },
      onPanelResize: {
        defaultValue: null,
        description: '',
        name: 'onPanelResize',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: '(width: number) => void'
        }
      },
      panelMinWidth: {
        defaultValue: {
          value: '0'
        },
        description: 'By default, **EuiInputPopovers** inherit the same width as the passed input element.\n' + 'However, if the input width is too small, you can pass a minimum panel width\n' + '(that should be based on the popover content).',
        name: 'panelMinWidth',
        parent: {
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      container: {
        defaultValue: null,
        description: "Restrict the popover's position within this element",
        name: 'container',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'HTMLElement'
        }
      },
      display: {
        defaultValue: {
          value: 'block'
        },
        description: 'CSS display type for both the popover and anchor',
        name: 'display',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Display'
        }
      },
      offset: {
        defaultValue: null,
        description: 'Distance away from the anchor that the popover will render',
        name: 'offset',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      panelRef: {
        defaultValue: null,
        description: '',
        name: 'panelRef',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLElement) => void'
        }
      },
      ownFocus: {
        defaultValue: {
          value: 'false'
        },
        description: 'Traps tab focus within the popover contents',
        name: 'ownFocus',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      focusTrapProps: {
        defaultValue: null,
        description: 'Object of props passed to EuiFocusTrap',
        name: 'focusTrapProps',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Partial<EuiFocusTrapProps>'
        }
      },
      isOpen: {
        defaultValue: null,
        description: 'Visibility state of the popover',
        name: 'isOpen',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      attachToAnchor: {
        defaultValue: {
          value: 'true'
        },
        description: 'Style and position alteration for arrow-less attachment.\n' + 'Intended for use with inputs as anchors, e.g. EuiInputPopover',
        name: 'attachToAnchor',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      closePopover: {
        defaultValue: null,
        description: 'Callback to handle hiding of the popover',
        name: 'closePopover',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: true,
        type: {
          name: 'NoArgCallback<void>'
        }
      },
      hasArrow: {
        defaultValue: null,
        description: 'Show arrow indicating to originating button',
        name: 'hasArrow',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      initialFocus: {
        defaultValue: null,
        description: 'Specifies what element should initially have focus; Can be a DOM\n' + 'node, or a selector string (which will be passed to\n' + 'document.querySelector() to find the DOM node), or a function that\n' + 'returns a DOM node.\n' + '\n' + 'If not passed, initial focus defaults to the popover panel.',
        name: 'initialFocus',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ElementTarget'
        }
      },
      insert: {
        defaultValue: null,
        description: 'Passed directly to EuiPortal for DOM positioning. Both properties are\n' + 'required if prop is specified',
        name: 'insert',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '{ sibling: HTMLElement; position: "after" | "before"; }'
        }
      },
      panelClassName: {
        defaultValue: null,
        description: 'Custom class added to the EuiPanel containing the popover contents',
        name: 'panelClassName',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      panelPaddingSize: {
        defaultValue: {
          value: 's'
        },
        description: 'EuiPanel padding on all sides',
        name: 'panelPaddingSize',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      panelStyle: {
        defaultValue: null,
        description: 'Standard DOM `style` attribute. Passed to the EuiPanel',
        name: 'panelStyle',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      panelProps: {
        defaultValue: null,
        description: 'Object of props passed to EuiPanel. See #EuiPopoverPanelProps',
        name: 'panelProps',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Omit<_EuiPanelDivlike, "color" | "style" | "hasBorder" | "hasShadow">'
        }
      },
      popoverScreenReaderText: {
        defaultValue: null,
        description: 'Optional screen reader instructions to announce upon popover open,\n' + "in addition to EUI's default popover instructions for Escape on close.\n" + 'Useful for popovers that may have additional keyboard capabilities such as\n' + 'arrow navigation.',
        name: 'popoverScreenReaderText',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      popoverRef: {
        defaultValue: null,
        description: '',
        name: 'popoverRef',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      },
      repositionOnScroll: {
        defaultValue: null,
        description: "When `true`, the popover's position is re-calculated when the user\n" + 'scrolls, this supports having fixed-position popover anchors. When nesting\n' + 'an `EuiPopover` in a scrollable container, `repositionOnScroll` should be `true`',
        name: 'repositionOnScroll',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      repositionToCrossAxis: {
        defaultValue: {
          value: 'false'
        },
        description: 'By default, popovers will attempt to position themselves along the initial\n' + 'axis specified. If there is not enough room either vertically or horizontally\n' + 'however, the popover will attempt to reposition itself along the secondary\n' + 'cross axis if there is room there instead.\n' + '\n' + 'If you do not not want this repositioning to occur (and it is acceptable for\n' + 'the popover to appear offscreen), set this to false to disable this behavior.',
        name: 'repositionToCrossAxis',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasDragDrop: {
        defaultValue: null,
        description: 'Must be set to true if using `EuiDragDropContext` within a popover,\n' + 'otherwise your nested drag & drop will have incorrect positioning',
        name: 'hasDragDrop',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      zIndex: {
        defaultValue: null,
        description: 'By default, popover content inherits the z-index of the anchor\n' + 'component; pass `zIndex` to override',
        name: 'zIndex',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      buffer: {
        defaultValue: {
          value: '16'
        },
        description: 'Minimum distance between the popover and the bounding container;\n' + 'Pass an array of 4 values to adjust each side differently: `[top, right, bottom, left]`',
        name: 'buffer',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number | [number, number, number, number]'
        }
      },
      arrowChildren: {
        defaultValue: null,
        description: 'Element to pass as the child element of the arrow;\n' + 'Use case is typically limited to an accompanying `EuiBeacon`',
        name: 'arrowChildren',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      onPositionChange: {
        defaultValue: null,
        description: 'Function callback for when the popover positon changes',
        name: 'onPositionChange',
        parent: {
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'eui/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '(position: EuiPopoverPosition) => void'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', '_EuiInputPopoverProps', 'EuiPopoverProps']
  };
} catch (e) {}